import { Middleware } from '@reduxjs/toolkit'
import { processHtmlForA11y, AccessibilityWarning } from './processHtmlForA11y'

export const A11Y_WARNINGS_DISPATCHED = 'a11y/warningsDispatched'
export const A11Y_VIOLATIONS_BLOCKED = 'a11y/violationsBlocked'
export const A11Y_FORCE_SAVE = 'a11y/forceSave'
export const A11Y_FORCE_SAVE_DONE = 'a11y/forceSaveDone'

export const BLOCKING_TYPES = new Set([
  'heading-skip',
  'empty-link',
  'empty-alt',
  'image-link-no-alt',
  'dead-link',
  'table-no-headers',
  'table-empty-header',
])

// Recursively find and process HTML string values in a plain object/array
function processPayloadHtml(value: unknown): {
  value: unknown
  warnings: AccessibilityWarning[]
} {
  const warnings: AccessibilityWarning[] = []

  if (typeof value === 'string' && /<[a-z][\s\S]*>/i.test(value)) {
    const result = processHtmlForA11y(value)
    warnings.push(...result.warnings)
    return { value: result.html, warnings }
  }

  if (Array.isArray(value)) {
    const processed = value.map(item => {
      const r = processPayloadHtml(item)
      warnings.push(...r.warnings)
      return r.value
    })
    return { value: processed, warnings }
  }

  if (value !== null && typeof value === 'object') {
    if (value instanceof FormData || value instanceof File || value instanceof Blob) {
      return { value, warnings }
    }
    const processed: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const r = processPayloadHtml(v)
      processed[k] = r.value
      warnings.push(...r.warnings)
    }
    return { value: processed, warnings }
  }

  return { value, warnings }
}

// Intercepts all ADD_*_START, UPDATE_*_START, CREATE_*_START action types
const SAVE_PATTERN = /^(ADD|UPDATE|CREATE)_.*_START$/

let pendingForceSaveAction: any = null
let bypassOnce = false

export const a11yMiddleware: Middleware = (store) => next => {
  window.addEventListener(A11Y_FORCE_SAVE, () => {
    if (pendingForceSaveAction) {
      bypassOnce = true
      const action = pendingForceSaveAction
      pendingForceSaveAction = null
      store.dispatch(action)
      window.dispatchEvent(new CustomEvent(A11Y_FORCE_SAVE_DONE))
    } else {
      // Only warnings present — save already succeeded; just clear the panel
      window.dispatchEvent(new CustomEvent(A11Y_FORCE_SAVE_DONE))
    }
  })

  return (action: any) => {
  if (bypassOnce) {
    bypassOnce = false
    return next(action)
  }
  if (action?.type && SAVE_PATTERN.test(action.type) && action.payload) {
    const { value: processedPayload, warnings } = processPayloadHtml(action.payload)

    const blockingWarnings = warnings.filter(w => BLOCKING_TYPES.has(w.type as any))
    const nonBlockingWarnings = warnings.filter(w => !BLOCKING_TYPES.has(w.type as any))

    if (blockingWarnings.length > 0) {
      pendingForceSaveAction = { ...action, payload: processedPayload }
      window.dispatchEvent(
        new CustomEvent(A11Y_VIOLATIONS_BLOCKED, { detail: blockingWarnings })
      )
      if (nonBlockingWarnings.length > 0) {
        window.dispatchEvent(
          new CustomEvent(A11Y_WARNINGS_DISPATCHED, { detail: nonBlockingWarnings })
        )
      }
      return
    }

    const result = next({ ...action, payload: processedPayload })

    if (nonBlockingWarnings.length > 0) {
      window.dispatchEvent(
        new CustomEvent(A11Y_WARNINGS_DISPATCHED, { detail: nonBlockingWarnings })
      )
    }

    return result
  }

  return next(action)
  }
}
